import * as React from "react";
import { cn } from "../lib/cn";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Marks the field as failed validation — red border, no extra layout shift (pair with a separately-rendered error message). */
  invalid?: boolean;
}

/**
 * A single-line text input matching the vintage-heritage token set — built
 * for Phase 8's login/reset-password pages, the first screen every user
 * (including non-technical staff on a phone) sees, so this gets the same
 * big-touch-target treatment as Button/NavItem (h-12, generous padding)
 * rather than a cramped default form control.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        // No ring-offset: the old ring-offset-2 floated the focus ring outside
        // the border with a visible gap, reading as two misaligned rectangles.
        // Flush ring (0 offset) + matching border color on focus makes it read
        // as one cohesive outline instead. Full-opacity ring-rust (not the
        // ring-rust/30 search-bar uses on a larger pill input) — at 30% here
        // it read as washed-out/dark rather than warm on a bordered field.
        // py-2 (on top of the fixed h-12/border-box height) gives placeholder
        // text and the caret real breathing room instead of hugging the edges.
        "h-12 w-full rounded-md border bg-cream px-4 py-2 text-base text-charcoal placeholder:text-muted transition-colors focus-visible:outline-none focus-visible:border-rust focus-visible:ring-2 focus-visible:ring-rust disabled:cursor-not-allowed disabled:opacity-50",
        invalid ? "border-danger" : "border-border-strong",
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = "Input";

export interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {}

export const Label = React.forwardRef<HTMLLabelElement, LabelProps>(({ className, ...props }, ref) => (
  <label ref={ref} className={cn("text-sm font-medium text-charcoal", className)} {...props} />
));
Label.displayName = "Label";
